import React, { useState, useEffect } from 'react';
import {
  Typography, TextField, InputAdornment, IconButton,
  DialogContent, DialogActions, Dialog, CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { FONT_BODY, FONT_DISPLAY, TYPE, RADIUS } from '../../styles/recruiterTheme';

// Fonts load from public/index.html — this only keeps the box-sizing reset.
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Page wrapper ── */
const PageWrap = styled.div`
  font-family: ${FONT_BODY};
  padding: 32px 32px 40px;
  background: #F3EEF9;
  min-height: 100%;
  animation: ${fadeUp} 0.4s ease both;
`;

/* ── Page header ── */
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-family: ${FONT_DISPLAY};
  font-size: ${TYPE.page}px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 500;
  line-height: 1.5;
  color: #8B7DB5;
  margin: 4px 0 0;
`;

/* ── Two-column layout ── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Section card ── */
const SectionCard = styled.div`
  background: #fff;
  border-radius: ${RADIUS.lg}px;
  border: 1px solid #EDE7F6;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(79,55,139,0.08);
  display: flex;
  flex-direction: column;
  min-width: 0;
  form { display: flex; flex-direction: column; flex: 1; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIcon = styled.div`
  width: 40px; height: 40px;
  border-radius: ${RADIUS.md}px;
  background: linear-gradient(135deg, #EDE7F6, #D8C8F5);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  svg { color: #6750A4; font-size: 20px; }
`;

const SectionTitle = styled.h2`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.section}px;
  font-weight: 700;
  line-height: 1.25;
  color: #2D1B5E;
  margin: 0;
`;

/* ── Avatar block ── */
const AvatarBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #F8F4FF, #F0EAF9);
  border-radius: ${RADIUS.md}px;
  border: 1px solid #EDE7F6;
  margin-bottom: 24px;
`;

const Avatar = styled.div`
  width: 48px; height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
  color: #fff;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.section}px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(79,55,139,0.3);
  flex-shrink: 0;
`;

const AvatarInfo = styled.div`
  min-width: 0;
`;

const AvatarName = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 700;
  line-height: 1.25;
  color: #2D1B5E;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AvatarEmail = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 500;
  line-height: 1.5;
  color: #8B7DB5;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* ── Field grid: two equal columns, collapses to one on narrow cards ── */
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  align-items: start;
  margin-bottom: 16px;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

/* ── Email read-only badge ── */
const EmailBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #F8F4FF;
  border: 1.5px solid #EDE7F6;
  border-radius: ${RADIUS.sm}px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const EmailBadgeLabel = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1.5;
  color: #8B7DB5;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: block;
`;

const EmailBadgeValue = styled.span`
  display: block;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 500;
  line-height: 1.5;
  color: #3D2B6B;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReadOnlyChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  margin-left: auto;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  color: #8B7DB5;
  background: #EDE7F6;
  border-radius: ${RADIUS.pill}px;
  padding: 0 8px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

/* ── Styled text field (size="small" → 40px, same height across a row) ── */
const StyledTextField = styled(TextField)`
  && {
    margin: 0;
    .MuiOutlinedInput-root {
      border-radius: ${RADIUS.sm}px;
      font-family: ${FONT_BODY};
      font-size: ${TYPE.body}px;
      min-height: 40px;
      background: #FAFAFA;
      transition: all 0.2s;
      &:hover fieldset { border-color: #7C5CBF; }
      &.Mui-focused fieldset { border-color: #6750A4; border-width: 2px; }
    }
    .MuiInputLabel-root {
      font-family: ${FONT_BODY};
      font-size: ${TYPE.body}px;
      &.Mui-focused { color: #6750A4; }
    }
    .MuiFormHelperText-root {
      font-family: ${FONT_BODY};
      font-size: ${TYPE.caption}px;
      margin: 4px 0 0 4px;
    }
  }
`;

/* ── Button row: right-aligned, pushed to the card bottom ── */
const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 8px;
`;

/* ── Submit button ── */
const SubmitBtn = styled.button`
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: ${RADIUS.sm}px;
  background: linear-gradient(135deg, #6750A4, #4F378B);
  color: #fff;
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.body}px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(79,55,139,0.32);
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 7px 20px rgba(79,55,139,0.42);
  }
  &:active { transform: translateY(0); }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    transform: none;
    box-shadow: none;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

/* ── Divider ── */
const Divider = styled.div`
  height: 1px;
  background: #F0EAF9;
  margin: 0 0 16px;
`;

/* ── Password strength bar ── */
const getStrength = (pwd) => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[@$!%*?&]/.test(pwd)) score++;
  return score;
};

const StrengthBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`;

const StrengthSegment = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 4px;
  background: ${p => p.active
    ? p.score <= 1 ? '#EF5350'
    : p.score === 2 ? '#FF9800'
    : p.score === 3 ? '#FFC107'
    : '#4CAF50'
    : '#EDE7F6'};
  transition: background 0.3s;
`;

const StrengthLabel = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 600;
  color: ${p =>
    p.score <= 1 ? '#EF5350'
    : p.score === 2 ? '#FF9800'
    : p.score === 3 ? '#FFC107'
    : '#4CAF50'};
  margin-left: 4px;
`;

/* ─── Component ─── */
const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('rbotUser')) || {};
  } catch {
    return {};
  }
};

const ProfileForm = ({ userData, onProfileUpdated }) => {
  // userData may be null until the parent finishes loading — fall back to the stored login.
  const sourceUser = userData || readStoredUser();
  const { name: initialName, phone: initialPhone, email: initialEmail } = sourceUser;

  const [name, setName] = useState(initialName || '');
  const [phone, setPhone] = useState(initialPhone || '');
  const [emailInput, setEmailInput] = useState(initialEmail || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Sync the form when userData arrives (or changes) after mount.
  useEffect(() => {
    if (!userData) return;
    setName(userData.name || '');
    setPhone(userData.phone || '');
    setEmailInput(userData.email || '');
  }, [userData?.name, userData?.phone, userData?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [dialog, setDialog] = useState({ open: false, type: '', message: '' });

  const updateProfileApiUrl = process.env.REACT_APP__UPDATE_PROFILE_API;
  const changePasswordApiUrl = process.env.REACT_APP__CHANGE_PASSWORD_API;

  const pwdStrength = getStrength(newPassword);
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];

  const openDialog = (type, message) => setDialog({ open: true, type, message });
  const closeDialog = () => setDialog({ open: false, type: '', message: '' });

  const handlePhoneChange = (e) => {
    const numeric = e.target.value.replace(/\D/g, '');
    if (numeric.length <= 10) setPhone(numeric);
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    if (savingProfile) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      openDialog('error', 'Please enter your full name.'); return;
    }
    if (phone && !/^\d{10}$/.test(phone)) {
      openDialog('error', 'Phone number must be exactly 10 digits.'); return;
    }
    setSavingProfile(true);
    try {
      const response = await fetch(updateProfileApiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput, name: trimmedName, phone }),
      });
      if (response.ok) {
        setName(trimmedName);
        try {
          const stored = readStoredUser();
          localStorage.setItem('rbotUser', JSON.stringify({ ...stored, name: trimmedName, phone }));
        } catch (storageError) {
          console.error('Failed to update stored user:', storageError);
        }
        if (typeof onProfileUpdated === 'function') {
          onProfileUpdated({ ...sourceUser, name: trimmedName, phone });
        }
        openDialog('success', 'Your profile details have been updated successfully.');
      } else {
        openDialog('error', 'Failed to update profile. Please try again.');
      }
    } catch (error) {
      openDialog('error', 'Something went wrong. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmPassword) {
      openDialog('error', 'Please fill in all password fields.'); return;
    }
    if (newPassword !== confirmPassword) {
      openDialog('error', 'New passwords do not match.'); return;
    }
    if (getStrength(newPassword) <= 1) {
      openDialog('error', 'Your new password is too weak. Use at least 8 characters with an uppercase letter, a number and a special character (@$!%*?&).'); return;
    }
    if (savingPassword) return;
    setSavingPassword(true);
    try {
      const response = await fetch(changePasswordApiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput, oldPassword, newPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        openDialog('success', data.message || 'Password updated successfully.');
        setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        openDialog('error', data.message === 'Incorrect old password.' ? 'The current password you entered is incorrect.' : 'Failed to update password.');
      }
    } catch (error) {
      openDialog('error', 'Something went wrong. Please try again.');
    } finally {
      setSavingPassword(false);
    }
  };

  const eyeProps = (show, toggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={toggle} edge="end" size="small">
          {show
            ? <VisibilityIcon sx={{ fontSize: 18, color: '#8B7DB5' }} />
            : <VisibilityOffIcon sx={{ fontSize: 18, color: '#8B7DB5' }} />}
        </IconButton>
      </InputAdornment>
    )
  });

  return (
    <>
      <GlobalStyle />
      <PageWrap>

        <PageHeader>
          <div>
            <PageTitle>My Profile</PageTitle>
            <PageSubtitle>Manage your personal information and account security</PageSubtitle>
          </div>
        </PageHeader>

        <Grid>
          {/* ── Profile Info ── */}
          <SectionCard>
            <SectionHeader>
              <SectionIcon><PersonOutlineIcon /></SectionIcon>
              <SectionTitle>Personal Information</SectionTitle>
            </SectionHeader>

            <AvatarBlock>
              <Avatar>{(name || initialName || '?').charAt(0).toUpperCase()}</Avatar>
              <AvatarInfo>
                <AvatarName title={name || initialName || ''}>{name || initialName || 'Your Name'}</AvatarName>
                <AvatarEmail title={emailInput}>{emailInput}</AvatarEmail>
              </AvatarInfo>
            </AvatarBlock>

            <form onSubmit={handleSubmitProfile}>
              <FieldGrid>
                <StyledTextField
                  label="Full name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  inputProps={{ maxLength: 30 }}
                />

                <StyledTextField
                  label="Phone number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={phone}
                  onChange={handlePhoneChange}
                  inputProps={{ maxLength: 10 }}
                  helperText={phone && phone.length < 10 ? `${phone.length}/10 digits` : ''}
                />
              </FieldGrid>

              <EmailBadge>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <EmailBadgeLabel>Email address</EmailBadgeLabel>
                  <EmailBadgeValue title={emailInput}>{emailInput}</EmailBadgeValue>
                </div>
                <ReadOnlyChip>Read-only</ReadOnlyChip>
              </EmailBadge>

              <ButtonRow>
                <SubmitBtn type="submit" disabled={savingProfile}>
                  {savingProfile && <CircularProgress size={16} sx={{ color: '#fff' }} />}
                  {savingProfile ? 'Saving…' : 'Save changes'}
                </SubmitBtn>
              </ButtonRow>
            </form>
          </SectionCard>

          {/* ── Change Password ── */}
          <SectionCard>
            <SectionHeader>
              <SectionIcon><LockOutlinedIcon /></SectionIcon>
              <SectionTitle>Change Password</SectionTitle>
            </SectionHeader>

            <EmailBadge style={{ marginBottom: 24 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <EmailBadgeLabel>Account</EmailBadgeLabel>
                <EmailBadgeValue title={emailInput}>{emailInput}</EmailBadgeValue>
              </div>
            </EmailBadge>

            <form onSubmit={handleSubmitPassword}>
              <FieldGrid>
                <FullRow>
                  <StyledTextField
                    label="Current password"
                    type={showOld ? 'text' : 'password'}
                    size="small"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    required
                    fullWidth
                    InputProps={eyeProps(showOld, () => setShowOld(v => !v))}
                  />
                </FullRow>
              </FieldGrid>

              <Divider />

              <FieldGrid>
                <div>
                  <StyledTextField
                    label="New password"
                    type={showNew ? 'text' : 'password'}
                    size="small"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    fullWidth
                    InputProps={eyeProps(showNew, () => setShowNew(v => !v))}
                  />

                  {/* Strength bar */}
                  {newPassword && (
                    <>
                      <StrengthBar>
                        {[1, 2, 3, 4].map(i => (
                          <StrengthSegment key={i} active={pwdStrength >= i} score={pwdStrength} />
                        ))}
                        <StrengthLabel score={pwdStrength}>{strengthLabels[pwdStrength]}</StrengthLabel>
                      </StrengthBar>
                    </>
                  )}
                </div>

                <StyledTextField
                  label="Confirm new password"
                  type={showConfirm ? 'text' : 'password'}
                  size="small"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  fullWidth
                  error={confirmPassword.length > 0 && confirmPassword !== newPassword}
                  helperText={confirmPassword.length > 0 && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
                  InputProps={eyeProps(showConfirm, () => setShowConfirm(v => !v))}
                />
              </FieldGrid>

              <ButtonRow>
                <SubmitBtn type="submit" disabled={savingPassword}>
                  {savingPassword && <CircularProgress size={16} sx={{ color: '#fff' }} />}
                  {savingPassword ? 'Updating…' : 'Update password'}
                </SubmitBtn>
              </ButtonRow>
            </form>
          </SectionCard>
        </Grid>

        {/* ── Dialog ── */}
        <Dialog
          open={dialog.open}
          onClose={closeDialog}
          PaperProps={{
            sx: {
              borderRadius: `${RADIUS.lg}px`,
              padding: '8px',
              boxShadow: '0 20px 60px rgba(79,55,139,0.18)',
              maxWidth: 360,
              width: '100%',
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: dialog.type === 'success'
                ? 'linear-gradient(135deg, #E6F4EA, #C8E6C9)'
                : 'linear-gradient(135deg, #FDECEA, #FFCDD2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              {dialog.type === 'success'
                ? <CheckCircleOutlineIcon sx={{ color: '#2D7D46', fontSize: 24 }} />
                : <ErrorOutlineIcon sx={{ color: '#B3261E', fontSize: 24 }} />}
            </div>
            <Typography sx={{
              fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25,
              color: '#2D1B5E', mb: 1
            }}>
              {dialog.type === 'success' ? 'Success!' : 'Something went wrong'}
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: '#5C4A8A', lineHeight: 1.5 }}>
              {dialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 1 }}>
            <button
              onClick={closeDialog}
              style={{
                minWidth: 160, height: 36, padding: '0 16px', borderRadius: RADIUS.sm,
                background: dialog.type === 'success'
                  ? 'linear-gradient(135deg, #6750A4, #4F378B)'
                  : 'linear-gradient(135deg, #E53935, #C62828)',
                border: 'none', color: '#fff',
                fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.body,
                cursor: 'pointer',
                boxShadow: dialog.type === 'success'
                  ? '0 4px 14px rgba(79,55,139,0.32)'
                  : '0 4px 14px rgba(229,57,53,0.28)',
              }}
            >
              {dialog.type === 'success' ? 'Great!' : 'Try again'}
            </button>
          </DialogActions>
        </Dialog>
      </PageWrap>
    </>
  );
};

export default ProfileForm;
